import React, { useState } from "react";
import axios from "axios";
import { baseUrl } from "../index";

/* ─────────────────────────────────────────────
   ALL styles are self-contained in this file.
   No external CSS, no Bootstrap dependency.
   Theme: #438c3d / #66bb6a
───────────────────────────────────────────── */

const S = {
  /* ── Tokens ── */
  color: {
    primary: "#438c3d",
    primaryDark: "#2f6629",
    primaryLight: "#66bb6a",
    primaryPale: "#eaf4e9",
    primaryGlass: "rgba(67,140,61,0.09)",
    surface: "#ffffff",
    surface2: "#f7fbf7",
    surface3: "#f0f8ef",
    textPrimary: "#1e3b1d",
    textSec: "#4a6649",
    textMuted: "#7a9578",
    border: "rgba(67,140,61,0.18)",
    borderStrong: "rgba(67,140,61,0.35)",
    danger: "#c0392b",
    dangerBg: "#fdecea",
  },
  radius: { sm: "6px", md: "12px", lg: "20px", xl: "30px" },
  shadow: {
    sm: "0 1px 4px rgba(67,140,61,0.10)",
    md: "0 4px 20px rgba(67,140,61,0.14),0 1px 4px rgba(0,0,0,0.06)",
    lg: "0 24px 80px rgba(11,28,10,0.30),0 6px 20px rgba(0,0,0,0.10)",
  },
  font: {
    display: "'DM Serif Display',Georgia,serif",
    body: "'DM Sans',system-ui,-apple-system,sans-serif",
  },
};

/* Google Fonts injected once via a <style> tag in the component */
const fontFaceCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

@keyframes ckyc-pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:0.4; transform:scale(0.84); }
}
@keyframes ckyc-fadein {
  from { opacity:0; transform:translateY(10px); }
  to   { opacity:1; transform:translateY(0); }
}
`;

/* ─────────────────────────────────────────────
   Inline style objects
───────────────────────────────────────────── */
const styles = {
  /* Backdrop / shell */
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 1055,
    overflowY: "auto",
    background:
      "radial-gradient(circle at 18% 14%, rgba(67,140,61,0.22),transparent 22%),radial-gradient(circle at 85% 10%, rgba(20,100,80,0.18),transparent 24%),linear-gradient(180deg,rgba(10,20,10,0.60),rgba(10,20,10,0.64))",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    padding: "20px 14px 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    animation: "ckyc-fadein 0.22s ease",
    fontFamily: S.font.body,
  },

  /* Dialog */
  dialog: {
    width: "100%",
    maxWidth: "960px",
    margin: "0 auto",
  },

  /* Card */
  card: {
    background: S.color.surface,
    borderRadius: S.radius.xl,
    overflow: "hidden",
    boxShadow: S.shadow.lg,
    border: "none",
    position: "relative",
  },

  /* Left accent stripe */
  stripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: "4px",
    background: `linear-gradient(180deg,${S.color.primaryLight},${S.color.primary})`,
    borderRadius: "4px 0 0 4px",
    zIndex: 2,
  },

  /* ── Hero ── */
  hero: {
    padding: "28px 32px 24px",
    background: `linear-gradient(135deg,${S.color.primaryDark} 0%,#1a5c38 50%,${S.color.primary} 100%)`,
    color: "#fff",
    position: "relative",
    overflow: "hidden",
  },
  heroGlow1: {
    position: "absolute",
    width: "220px",
    height: "220px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.07)",
    top: "-80px",
    right: "-60px",
    pointerEvents: "none",
  },
  heroGlow2: {
    position: "absolute",
    width: "110px",
    height: "110px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    bottom: "-28px",
    left: "42%",
    pointerEvents: "none",
  },
  heroBadge: {
    display: "inline-block",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    color: S.color.primaryLight,
    marginBottom: "8px",
    position: "relative",
    zIndex: 1,
  },
  heroTitle: {
    fontFamily: S.font.display,
    fontSize: "26px",
    fontWeight: 400,
    color: "#fff",
    margin: "0 0 8px",
    lineHeight: 1.2,
    position: "relative",
    zIndex: 1,
  },
  heroSub: {
    fontSize: "13.5px",
    color: "rgba(255,255,255,0.72)",
    margin: 0,
    lineHeight: 1.55,
    position: "relative",
    zIndex: 1,
  },
  heroSubBold: {
    color: "#fff",
    fontWeight: 500,
  },

  /* ── Body / grid ── */
  body: {
    padding: "28px 32px 32px",
    background: `linear-gradient(180deg,${S.color.surface} 0%,${S.color.surface2} 100%)`,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "260px 1fr",
    gap: "28px",
    alignItems: "start",
  },

  /* ── Aside panel ── */
  aside: {
    background: S.color.surface3,
    border: `1px solid ${S.color.border}`,
    borderRadius: S.radius.lg,
    padding: "22px 20px",
  },
  asideEyebrow: {
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: S.color.textMuted,
    marginBottom: "8px",
  },
  asideTitle: {
    fontFamily: S.font.display,
    fontSize: "17px",
    fontWeight: 400,
    color: S.color.textPrimary,
    lineHeight: 1.35,
    marginBottom: "10px",
  },
  asideText: {
    fontSize: "13px",
    color: S.color.textSec,
    lineHeight: 1.65,
    marginBottom: "18px",
  },

  /* Checklist */
  checklist: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  checkItem: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    fontSize: "12.5px",
    color: S.color.textSec,
    fontWeight: 400,
  },
  checkDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: S.color.primaryLight,
    flexShrink: 0,
    animation: "ckyc-pulse 2.4s ease-in-out infinite",
  },

  /* ── Main upload column ── */
  mainHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "20px",
    gap: "12px",
  },
  mainTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: S.color.textPrimary,
  },
  mainSub: {
    fontSize: "12px",
    color: S.color.textMuted,
    marginTop: "2px",
  },

  /* Close button */
  closeBtn: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: S.color.surface3,
    border: `1px solid ${S.color.border}`,
    color: S.color.textSec,
    fontSize: "18px",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
    transition: "all 0.2s",
  },
  closeBtnHover: {
    background: S.color.dangerBg,
    borderColor: "rgba(192,57,43,0.3)",
    color: S.color.danger,
  },

  /* Upload label section */
  uploadLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: S.color.textSec,
    marginBottom: "8px",
    display: "block",
  },

  /* Drop zone */
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "36px 20px",
    background: S.color.surface3,
    border: `1.5px dashed ${S.color.borderStrong}`,
    borderRadius: S.radius.lg,
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.22s",
  },
  dropzoneHover: {
    background: S.color.primaryPale,
    borderColor: S.color.primary,
    boxShadow: `0 0 0 4px ${S.color.primaryGlass}`,
  },
  dropzoneIcon: {
    width: "52px",
    height: "52px",
    borderRadius: S.radius.md,
    background: "rgba(67,140,61,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.22s",
  },
  dropzoneIconImg: {
    width: "24px",
    height: "24px",
    filter:
      "invert(33%) sepia(55%) saturate(600%) hue-rotate(88deg) brightness(80%)",
  },
  dropzoneTitle: {
    fontSize: "14px",
    fontWeight: 500,
    color: S.color.textPrimary,
    margin: 0,
  },
  dropzoneSub: {
    fontSize: "12px",
    color: S.color.textMuted,
    margin: "3px 0 0",
  },

  /* Preview pane */
  preview: {
    position: "relative",
    borderRadius: S.radius.lg,
    overflow: "hidden",
    border: `1px solid ${S.color.borderStrong}`,
    background: S.color.surface2,
    boxShadow: S.shadow.sm,
  },
  previewImg: {
    width: "100%",
    display: "block",
    maxHeight: "220px",
    objectFit: "cover",
  },
  previewLabel: {
    fontSize: "11px",
    fontWeight: 500,
    color: S.color.textMuted,
    textAlign: "center",
    padding: "8px 0",
    background: S.color.surface3,
    borderTop: `1px solid ${S.color.border}`,
    letterSpacing: "0.04em",
  },
  previewClose: {
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(0,0,0,0.12)",
    color: "#555",
    fontSize: "16px",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
    padding: 0,
    boxShadow: "0 2px 8px rgba(0,0,0,0.14)",
    transition: "all 0.2s",
  },

  /* Actions row */
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginTop: "18px",
    paddingTop: "4px",
  },
  actionsHint: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "12.5px",
    color: S.color.textMuted,
  },
  pulseDot: {
    display: "inline-block",
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: S.color.primaryLight,
    flexShrink: 0,
    animation: "ckyc-pulse 2.4s ease-in-out infinite",
  },

  /* Upload button */
  uploadBtn: {
    fontFamily: S.font.body,
    fontSize: "14px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: "#fff",
    background: `linear-gradient(135deg,${S.color.primaryLight} 0%,${S.color.primary} 100%)`,
    border: "none",
    borderRadius: S.radius.md,
    padding: "11px 28px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: `0 2px 12px rgba(67,140,61,0.35),0 1px 3px rgba(0,0,0,0.08)`,
    transition: "filter 0.22s,box-shadow 0.22s,transform 0.22s",
  },
  uploadBtnDisabled: {
    opacity: 0.42,
    cursor: "not-allowed",
    boxShadow: "none",
  },
};

/* ─────────────────────────────────────────────
   Hover-state helper hook
───────────────────────────────────────────── */
function useHover() {
  const [hovered, setHovered] = useState(false);
  return [
    hovered,
    {
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    },
  ];
}

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
const UploadDoc = (props) => {
  const [photo, setPhoto] = useState("");
  const [dropHover, dropHoverEvts] = useHover();
  const [closeHover, closeHoverEvts] = useHover();
  const [btnHover, btnHoverEvts] = useHover();

  const uploadId = props.title === "photo" ? "photo" : "form60";
  const uploadLabel = props.title === "photo" ? "Photo" : "Form 60";

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const onSubmit = (e) => {
    e.preventDefault();
    const files = document.querySelectorAll("input[type=file]");
    const formData = new FormData();
    formData.append("insurerID", props.Companyid);
    formData.append("enquiryNo", props.EnquiryNo);
    formData.append("URL", "UploadCKYCFile");

    files.forEach((f) => {
      if (f.value !== "") formData.append(f.id?.toLowerCase(), f.files[0]);
    });

    axios
      .post(`${baseUrl}/services/call/UploadFile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        if (res.data.StatusCode === 200) {
          props.setKYCDetails({
            Base64Photo: "",
            Base64POI: "",
            Base64POA: "",
            IsRedirectUrl: false,
            IsUploadFile: true,
            SelectedPOA: "",
            SelectedPOI: "",
            SelectedPOIValue: "",
            selectedPOAValue: "",
          });
        } else {
          alert("Kindly Choose File");
        }
      });
  };

  return (
    <>
      {/* Inject keyframes + Google Fonts once */}
      <style>{fontFaceCSS}</style>

      {/* Backdrop */}
      <div
        style={styles.backdrop}
        tabIndex="-1"
        aria-labelledby="ckycModalLabel"
        aria-modal="true"
        role="dialog"
      >
        {/* Dialog */}
        <div style={styles.dialog}>
          {/* Card */}
          <div style={styles.card}>
            {/* Left accent stripe */}
            <div style={styles.stripe} />

            {/* ── Hero ── */}
            <div style={styles.hero}>
              <div style={styles.heroGlow1} />
              <div style={styles.heroGlow2} />
              <div style={styles.heroBadge}>Secure verification</div>
              <h1 style={styles.heroTitle} id="ckycModalLabel">
                Upload Documents
              </h1>
              <p style={styles.heroSub}>
                Upload your <b style={styles.heroSubBold}>{uploadLabel}</b> to
                continue the CKYC verification journey.
              </p>
            </div>

            {/* ── Body ── */}
            <div style={styles.body}>
              <div style={styles.grid}>
                {/* Aside */}
                <div style={styles.aside}>
                  <div style={styles.asideEyebrow}>Why we ask this</div>
                  <div style={styles.asideTitle}>
                    Keep the flow fast, secure, and compliant.
                  </div>
                  <div style={styles.asideText}>
                    A clear photo or form submission helps us complete
                    verification with fewer delays.
                  </div>
                  <div style={styles.checklist}>
                    {[
                      "Use a clear, uncropped image",
                      "Keep text readable and upright",
                      "Upload a single file to proceed",
                    ].map((txt) => (
                      <div key={txt} style={styles.checkItem}>
                        <span style={styles.checkDot} />
                        {txt}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Main upload column */}
                <div>
                  {/* Head row */}
                  <div style={styles.mainHead}>
                    <div>
                      <div style={styles.mainTitle}>Document Upload</div>
                      <div style={styles.mainSub}>
                        Clear, readable images help approvals move faster.
                      </div>
                    </div>
                    <button
                      type="button"
                      style={{
                        ...styles.closeBtn,
                        ...(closeHover ? styles.closeBtnHover : {}),
                      }}
                      onClick={() => props.onKycSubmit(false)}
                      aria-label="Close"
                      {...closeHoverEvts}
                    >
                      ×
                    </button>
                  </div>

                  {/* Form */}
                  <form onSubmit={onSubmit}>
                    <span style={styles.uploadLabel}>Select file</span>

                    {/* Drop zone */}
                    {photo === "" && (
                      <div>
                        <input
                          type="file"
                          name={uploadLabel.toLowerCase()}
                          onChange={async (e) =>
                            setPhoto(await toBase64(e.target.files[0]))
                          }
                          id={uploadId}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor={uploadId}
                          style={{
                            ...styles.dropzone,
                            ...(dropHover ? styles.dropzoneHover : {}),
                          }}
                          {...dropHoverEvts}
                        >
                          <div
                            style={{
                              ...styles.dropzoneIcon,
                              ...(dropHover
                                ? {
                                    background: "rgba(67,140,61,0.20)",
                                    transform: "translateY(-2px)",
                                  }
                                : {}),
                            }}
                          >
                            <img
                              src={require("../Images/upload.svg")}
                              alt="Upload"
                              style={styles.dropzoneIconImg}
                            />
                          </div>
                          <div>
                            <p style={styles.dropzoneTitle}>
                              Click to upload file
                            </p>
                            <p style={styles.dropzoneSub}>
                              PNG, JPG or JPEG. Keep the details visible and
                              upright.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Preview */}
                    {photo !== "" && (
                      <div style={styles.preview}>
                        <button
                          type="button"
                          style={styles.previewClose}
                          onClick={() => setPhoto("")}
                          aria-label="Remove file"
                        >
                          ×
                        </button>
                        <img
                          src={photo}
                          title={props.title}
                          alt={`${props.title} preview`}
                          style={styles.previewImg}
                        />
                        <div style={styles.previewLabel}>
                          Uploaded file preview
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={styles.actions}>
                      <div style={styles.actionsHint}>
                        <span style={styles.pulseDot} />
                        One file upload required to proceed
                      </div>
                      <button
                        type="submit"
                        disabled={photo === ""}
                        style={{
                          ...styles.uploadBtn,
                          ...(photo === "" ? styles.uploadBtnDisabled : {}),
                          ...(btnHover && photo !== ""
                            ? {
                                filter: "brightness(1.08)",
                                boxShadow: "0 6px 20px rgba(67,140,61,0.45)",
                                transform: "translateY(-1px)",
                              }
                            : {}),
                        }}
                        {...(photo !== "" ? btnHoverEvts : {})}
                      >
                        Upload Document
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          {/* /card */}
        </div>
        {/* /dialog */}
      </div>
      {/* /backdrop */}
    </>
  );
};

export default UploadDoc;
